'use client';

import { useState, useEffect } from 'react';
import { AlertCircle, Search, RefreshCw, ExternalLink, MessageSquare } from 'lucide-react';
import { useTranslation } from '@/providers/I18nProvider';
import ConversationModal from './ConversationModal';

interface ErrorLog {
  errorId: string;
  timestamp: string;
  errorType: 'LLM_ERROR' | 'TOOL_ERROR' | 'USER_FEEDBACK' | 'SECURITY_VIOLATION' | 'THREAT_DETECTION' | 'ABUSE_DETECTION';
  errorMessage: string;
  errorDetails?: {
    tool?: string;
    statusCode?: number;
    provider?: string;
    model?: string;
    requestBody?: any;
    responseBody?: any;
    messageId?: string;
    feedbackType?: string;
    feedbackText?: string;
    [key: string]: any;
  };
  userId?: string;
  conversationId?: string;
  userMessage?: string;
  stackTrace?: string;
}

interface ErrorLogsProps {
  agentId: string;
  agentName: string;
}

const ERROR_TYPE_COLORS = {
  LLM_ERROR: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400',
  TOOL_ERROR: 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400',
  USER_FEEDBACK: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400',
  SECURITY_VIOLATION: 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400',
  THREAT_DETECTION: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400',
  ABUSE_DETECTION: 'bg-pink-100 text-pink-800 dark:bg-pink-900/20 dark:text-pink-400',
};

const ERROR_TYPE_LABELS = {
  LLM_ERROR: 'LLM Error',
  TOOL_ERROR: 'Tool Error',
  USER_FEEDBACK: 'User Feedback',
  SECURITY_VIOLATION: 'Security',
  THREAT_DETECTION: 'Threat',
  ABUSE_DETECTION: 'Abuse',
};

export default function ErrorLogs({ agentId, agentName }: ErrorLogsProps) {
  const { t } = useTranslation();
  const [errors, setErrors] = useState<ErrorLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedErrorType, setSelectedErrorType] = useState<string>('all');
  const [selectedError, setSelectedError] = useState<ErrorLog | null>(null);
  const [loadingErrorDetails, setLoadingErrorDetails] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);

  const fetchErrors = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/chat/agents/${agentId}/errors`);
      if (response.ok) {
        const data = await response.json();
        setErrors(data.errors || []);
      }
    } catch (error) {
      console.error('Failed to fetch errors:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchErrors();
  }, [agentId]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchErrors();
  };

  const filteredErrors = errors.filter(error => {
    const matchesSearch = !searchTerm || 
      error.errorMessage.toLowerCase().includes(searchTerm.toLowerCase()) ||
      error.userId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      error.conversationId?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = selectedErrorType === 'all' || error.errorType === selectedErrorType;
    
    return matchesSearch && matchesType;
  });

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const truncateMessage = (message: string, maxLength: number = 100) => {
    if (message.length <= maxLength) return message;
    return message.substring(0, maxLength) + '...';
  };

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-6xl mx-auto p-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                {t('admin.logs.title')}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {t('admin.logs.description')}
              </p>
            </div>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              {t('admin.logs.refresh')}
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder={t('admin.logs.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
          <select
            value={selectedErrorType}
            onChange={(e) => setSelectedErrorType(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
          >
            <option value="all">{t('admin.logs.filters.allTypes')}</option>
            <option value="LLM_ERROR">{t('admin.logs.filters.llmError')}</option>
            <option value="TOOL_ERROR">{t('admin.logs.filters.toolError')}</option>
            <option value="USER_FEEDBACK">{t('admin.logs.filters.userFeedback')}</option>
            <option value="SECURITY_VIOLATION">{t('admin.logs.filters.securityViolation')}</option>
            <option value="THREAT_DETECTION">{t('admin.logs.filters.threatDetection')}</option>
            <option value="ABUSE_DETECTION">{t('admin.logs.filters.abuseDetection')}</option>
          </select>
          </div>
        </div>

        {/* Error List */}
        {loading ? (
          <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 overflow-hidden">
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {[1, 2, 3, 4, 5].map((index) => (
                <div key={index} className="px-6 py-4 animate-pulse">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="h-6 w-20 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
                        <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
                        <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
                      </div>
                      <div className="h-5 w-3/4 bg-gray-200 dark:bg-gray-700 rounded mb-1"></div>
                      <div className="h-4 w-1/2 bg-gray-200 dark:bg-gray-700 rounded"></div>
                      <div className="flex items-center gap-4 mt-2">
                        <div className="h-3 w-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
                        <div className="h-3 w-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
                      </div>
                    </div>
                    <div className="h-4 w-4 bg-gray-200 dark:bg-gray-700 rounded ml-4"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : filteredErrors.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500 dark:text-gray-400">
            <AlertCircle className="h-12 w-12 mb-4" />
            <p className="text-lg font-medium">{t('admin.logs.empty.noErrors')}</p>
            <p className="text-sm mt-1">
              {searchTerm || selectedErrorType !== 'all' ? t('admin.logs.empty.tryAdjusting') : t('admin.logs.empty.noErrorsMessage')}
            </p>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 overflow-hidden">
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredErrors.map((error) => (
                  <div
                    key={error.errorId}
                    className="px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
                    onClick={async () => {
                      setLoadingErrorDetails(true);
                      try {
                        // Fetch full error details from S3
                        const response = await fetch(`/api/chat/agents/${agentId}/errors?errorId=${error.errorId}`);
                        if (response.ok) {
                          const data = await response.json();
                          setSelectedError(data.error);
                        } else {
                          // Fallback to metadata only
                          setSelectedError(error);
                        }
                      } catch (err) {
                        console.error('Failed to load error details:', err);
                        // Fallback to metadata only
                        setSelectedError(error);
                      } finally {
                        setLoadingErrorDetails(false);
                      }
                    }}
                  >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${ERROR_TYPE_COLORS[error.errorType] || 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'}`}>
                        {ERROR_TYPE_LABELS[error.errorType] || error.errorType}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {formatTimestamp(error.timestamp)}
                      </span>
                      {error.errorDetails?.provider && (
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {error.errorDetails.provider} {error.errorDetails.model && `- ${error.errorDetails.model}`}
                        </span>
                      )}
                    </div>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
                      {truncateMessage(error.errorMessage)}
                    </p>
                    {error.userMessage && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {t('admin.logs.userMessagePreview')}: "{truncateMessage(error.userMessage, 80)}"
                      </p>
                    )}
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500 dark:text-gray-400">
                      {error.userId && <span>{t('admin.logs.details.userId')}: {error.userId}</span>}
                      {error.conversationId && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedConversationId(error.conversationId!);
                          }}
                          className="flex items-center gap-1 text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                        >
                          <MessageSquare className="h-3 w-3" />
                          <span className="underline">{error.conversationId}</span>
                        </button>
                      )}
                    </div>
                  </div>
                  <ExternalLink className="h-4 w-4 text-gray-400 ml-4" />
                  </div>
                </div>
              ))}
              </div>
            </div>
        )}
      {/* Loading Modal */}
      {loadingErrorDetails && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl p-8">
            <div className="flex items-center gap-3">
              <RefreshCw className="h-5 w-5 animate-spin text-blue-500" />
              <p className="text-gray-900 dark:text-gray-100">{t('admin.logs.loadingDetails')}</p>
            </div>
          </div>
        </div>
      )}

      {/* Error Detail Modal */}
      {selectedError && !loadingErrorDetails && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedError(null)}>
          <div 
            className="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('admin.logs.details.title')}</h2>
                <button
                  onClick={() => setSelectedError(null)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="p-6 overflow-auto max-h-[calc(90vh-5rem)]">
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('admin.logs.details.errorType')}</h3>
                  <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${ERROR_TYPE_COLORS[selectedError.errorType]}`}>
                    {selectedError.errorType.replace('_', ' ')}
                  </span>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('admin.logs.details.timestamp')}</h3>
                  <p className="text-sm text-gray-900 dark:text-gray-100">{formatTimestamp(selectedError.timestamp)}</p>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('admin.logs.details.errorMessage')}</h3>
                  <p className="text-sm text-gray-900 dark:text-gray-100">{selectedError.errorMessage}</p>
                </div>
                
                {selectedError.userMessage && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('admin.logs.details.userMessage')}</h3>
                    <p className="text-sm text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">
                      {selectedError.userMessage}
                    </p>
                  </div>
                )}
                
                {selectedError.errorDetails && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('admin.logs.details.errorDetails')}</h3>
                    <pre className="text-xs text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-gray-800 p-3 rounded-lg overflow-auto">
                      {JSON.stringify(selectedError.errorDetails, null, 2)}
                    </pre>
                  </div>
                )}
                
                {selectedError.stackTrace && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('admin.logs.details.stackTrace')}</h3>
                    <pre className="text-xs text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-gray-800 p-3 rounded-lg overflow-auto">
                      {selectedError.stackTrace}
                    </pre>
                  </div>
                )}
                
                <div className="flex gap-4 text-sm">
                  {selectedError.userId && (
                    <div>
                      <span className="text-gray-700 dark:text-gray-300">{t('admin.logs.details.userId')}:</span>
                      <span className="ml-2 text-gray-900 dark:text-gray-100">{selectedError.userId}</span>
                    </div>
                  )}
                  {selectedError.conversationId && (
                    <div>
                      <span className="text-gray-700 dark:text-gray-300">{t('admin.logs.details.conversationId')}:</span>
                      <button
                        onClick={() => {
                          setSelectedError(null);
                          setSelectedConversationId(selectedError.conversationId!);
                        }}
                        className="ml-2 text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 underline flex items-center gap-1 inline-flex"
                      >
                        <MessageSquare className="h-3 w-3" />
                        <span>{selectedError.conversationId}</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Conversation Modal */}
      {selectedConversationId && (
        <ConversationModal
          conversationId={selectedConversationId}
          agentId={agentId}
          onClose={() => setSelectedConversationId(null)}
        />
      )}
      </div>
    </div>
  );
}